import { useEffect, useState } from 'react'
import { zonedDayBounds } from '../lib/clock'
import { CalendarRange } from 'lucide-react'
import { format, subDays } from 'date-fns'
import { useTranslation } from 'react-i18next'

// Controle compartilhado de período: pills de preset OU intervalo personalizado.
// Controlado: value = {preset} | {from,to} (ISO), onChange recebe o mesmo shape.
const PRESETS = [
  { key: 'today', labelKey: 'common:time.today' },
  { key: 'current_shift', labelKey: 'common:time.current_shift' },
  { key: '24h', labelKey: 'common:time.last_24h' },
  { key: '7d', labelKey: 'common:time.last_7d' },
  { key: '30d', labelKey: 'common:time.last_30d' },
  { key: 'month', labelKey: 'common:time.month' },
]

// O input de data devolve um DIA; o backend espera um instante, e resolve toda janela
// no fuso da MINA em escopo (devolvido em window.timezone). Montar a string com "Z"
// mandava o dia como se fosse UTC: numa mina em UTC-3, "18/08 a 18/08" cobria das 21h
// do dia 17 às 20h59 do 18, engolindo o turno da noite anterior e perdendo as três
// últimas horas. Fechar no fuso do NAVEGADOR corrige isso para quem está na mina e
// repete o mesmo erro, menor, para quem abre o painel de outro fuso: o corte tem que
// ser o mesmo que o servidor faz.
const dayBounds = (day, tz) => zonedDayBounds(day, tz)

export default function DateRangePicker({ value, onChange, timezone = '' }) {
  const { t } = useTranslation()
  const preset = value?.preset ?? null
  const [customOpen, setCustomOpen] = useState(preset == null)
  const [from, setFrom] = useState(value?.from ? value.from.slice(0, 10) : format(subDays(new Date(), 7), 'yyyy-MM-dd'))
  const [to, setTo] = useState(value?.to ? value.to.slice(0, 10) : format(new Date(), 'yyyy-MM-dd'))

  useEffect(() => { if (preset != null) setCustomOpen(false) }, [preset])

  const pickPreset = (key) => {
    setCustomOpen(false)
    onChange?.({ preset: key })
  }

  const openCustom = () => {
    setCustomOpen(true)
    if (from && to && from <= to) {
      onChange?.({ from: dayBounds(from, timezone).from, to: dayBounds(to, timezone).to })
    }
  }

  const changeDay = (which, day) => {
    const nextFrom = which === 'from' ? day : from
    const nextTo = which === 'to' ? day : to
    if (which === 'from') setFrom(day); else setTo(day)
    if (nextFrom && nextTo && nextFrom <= nextTo) {
      onChange?.({ from: dayBounds(nextFrom, timezone).from, to: dayBounds(nextTo, timezone).to })
    }
  }

  const custom = customOpen || preset == null

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        {PRESETS.map(p => (
          <button key={p.key} onClick={() => pickPreset(p.key)}
            className={`px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${!custom && preset === p.key
              ? 'bg-brand text-white'
              : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
            {t(p.labelKey)}
          </button>
        ))}
        <button onClick={openCustom}
          className={`px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors inline-flex items-center gap-1.5 ${custom
            ? 'bg-brand text-white'
            : 'bg-white dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
          <CalendarRange size={13} /> {t('common:time.custom')}
        </button>
      </div>

      {custom && (
        <div className="flex items-center gap-1.5 text-sm">
          <input type="date" value={from} max={to || undefined} onChange={e => changeDay('from', e.target.value)}
            className="px-2 py-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand/30" />
          <span className="text-slate-400 dark:text-slate-500">–</span>
          <input type="date" value={to} min={from || undefined} onChange={e => changeDay('to', e.target.value)}
            className="px-2 py-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 focus:outline-none focus:ring-2 focus:ring-brand/30" />
        </div>
      )}
    </div>
  )
}
